import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer.js";
import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass.js";
import { UnrealBloomPass } from "three/examples/jsm/postprocessing/UnrealBloomPass.js";
import { ShaderPass } from "three/examples/jsm/postprocessing/ShaderPass.js";
import { RGBShiftShader } from "three/examples/jsm/shaders/RGBShiftShader.js";
import code from "../../assets/code3.glb";
import allGlbVert from "../shaders/allGlbVert.glsl";
import appGlbFrag from "../shaders/appGlbFrag.glsl";
import appBgVert from "../shaders/allBgVert.glsl";
import appBgFrag from "../shaders/appBgFrag.glsl";

export function createScene({ renderer, camera, loadingManager } = {}) {
  const scene = new THREE.Scene();
  scene.background = new THREE.Color(0x030303);

  const modelRoot = new THREE.Group();
  scene.add(modelRoot);

  const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
  const isCoarse =
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(pointer: coarse)").matches;
  const isMobile =
    /Mobi|Android|iPhone|iPad|iPod|Opera Mini|IEMobile/i.test(ua) || isCoarse;

  const MODEL_SCALE = isMobile ? 0.2 : 0.5;
  const MODEL_POS_X = isMobile ? 0.0 : 0.0;
  const MODEL_POS_Y = isMobile ? -0.1 : -0.35;
  const MAX_POINTS = isMobile ? 9000 : 20000;
  const BG_COUNT = isMobile ? 1000 : 1500;
  const MAX_PIXEL_RATIO = isMobile ? 1.0 : 2.0;
  const DEFAULT_POINT_SIZE = isMobile ? 8.0 : 11.0;
  const DEFAULT_BLOOM_STRENGTH = isMobile ? 0.8 : 0.1;

  const composer = new EffectComposer(renderer);

  const isHighRes = window.innerWidth > 2500;

  const sizes = {
    width: Math.max(1, window.innerWidth),
    height: Math.max(1, window.innerHeight),
    pixelRatio: isHighRes ? 1.0 : Math.min(window.devicePixelRatio || 1, 2.0),
  };
  composer.setSize(sizes.width, sizes.height);
  renderer.setPixelRatio(sizes.pixelRatio);
  if (composer.setPixelRatio) {
    composer.setPixelRatio(sizes.pixelRatio);
  }

  const winW = Math.max(1, window.innerWidth);
  const winH = Math.max(1, window.innerHeight);
  composer.setSize(winW, winH);
  composer.addPass(new RenderPass(scene, camera));

  const bloomPass = new UnrealBloomPass(
    new THREE.Vector2(winW, winH),
    DEFAULT_BLOOM_STRENGTH,
    0.3,
    0.05,
  );
  composer.addPass(bloomPass);

  const chromaticPass = new ShaderPass(RGBShiftShader);
  chromaticPass.uniforms["amount"].value = 0.0015;
  composer.addPass(chromaticPass);
  composer.renderToScreen = true;

  const quadGeo = new THREE.PlaneGeometry(2, 2);
  const quadScene = new THREE.Scene();
  const quadCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);

  const GRAIN_AMOUNT = isMobile ? 0.12 : 0.3;
  const GRAIN_SIZE = isMobile ? 2.0 : 1.2;

  const grainUniforms = {
    tDiffuse: { value: null },
    time: { value: 0.0 },
    amount: { value: GRAIN_AMOUNT },
    resolution: { value: new THREE.Vector2(sizes.width, sizes.height) },
    grainSize: { value: GRAIN_SIZE },
    lumInfluence: { value: 0.7 },
  };

  const grainMaterial = new THREE.ShaderMaterial({
    uniforms: grainUniforms,
    vertexShader:
      "varying vec2 vUv;void main(){vUv=uv;gl_Position=vec4(position,1.0);}",
    fragmentShader:
      "precision highp float;uniform sampler2D tDiffuse;uniform float time;uniform float amount;uniform vec2 resolution;uniform float grainSize;uniform float lumInfluence;varying vec2 vUv;float rand(vec2 n){return fract(sin(dot(n,vec2(12.9898,4.1414)))*43758.5453);}float organicNoise(vec2 uv,float timeStep){float n1=rand(uv+vec2(timeStep*0.013,timeStep*0.071));float n2=rand(uv*0.7+vec2(timeStep*0.064,timeStep*0.021));return (n1+n2)*0.5;}float overlay(float base,float blend){return (base<0.5)?(2.0*base*blend):(1.0-2.0*(1.0-base)*(1.0-blend));}vec3 blendOverlay(vec3 base,vec3 blend){return vec3(overlay(base.r,blend.r),overlay(base.g,blend.g),overlay(base.b,blend.b));}void main(){vec4 color=texture2D(tDiffuse,vUv);float timeStep=floor(time*24.0);vec2 grainCoord=gl_FragCoord.xy/grainSize;float noiseValue=organicNoise(grainCoord,timeStep);float blendNoise=0.5+(noiseValue-0.5)*amount;vec3 overlayColor=blendOverlay(color.rgb,vec3(blendNoise));float luminance=dot(color.rgb,vec3(0.299,0.587,0.114));float noiseMask=pow(luminance,0.5)*(1.0-luminance);float finalBlend=mix(1.0,noiseMask,lumInfluence);vec3 noiseRedux=(vec3(noiseValue)-0.5)*amount;vec3 finalColor=color.rgb+noiseRedux;gl_FragColor=vec4(finalColor,color.a);}",
    depthTest: false,
    depthWrite: false,
    transparent: false,
  });

  const grainQuad = new THREE.Mesh(quadGeo, grainMaterial);
  quadScene.add(grainQuad);

  const grainPass = {
    enabled: true,
    renderToScreen: true,
    setSize(width, height) {
      grainUniforms.resolution.value.set(width, height);
    },
    render(rendererRef, writeBuffer, readBuffer, delta) {
      grainUniforms.tDiffuse.value = readBuffer.texture;
      grainUniforms.time.value += delta;
      if (this.renderToScreen) {
        rendererRef.setRenderTarget(null);
        rendererRef.render(quadScene, quadCamera);
      } else {
        rendererRef.setRenderTarget(writeBuffer);
        rendererRef.clear();
        rendererRef.render(quadScene, quadCamera);
      }
    },
  };

  composer.addPass(grainPass);

  let resolveReady;
  const ready = new Promise((res) => (resolveReady = res));

  const loader = new GLTFLoader(loadingManager);

  const mouseNDC = new THREE.Vector2(-9999, -9999);
  const raycaster = new THREE.Raycaster();
  const interactionPlane = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0);
  const mouseWorldPos = new THREE.Vector3();

  const uniforms = {
    time: { value: 0 },
    size: { value: DEFAULT_POINT_SIZE },
    pixelRatio: {
      value: Math.min(window.devicePixelRatio || 1, MAX_PIXEL_RATIO),
    },
    brightness: { value: 2.0 },
    progress: { value: 0.0 },
    uMobile: { value: isMobile ? 1.0 : 0.0 },
  };

  const bgCount = BG_COUNT;
  const spread = 12;

  const bgPosData = new Float32Array(bgCount * 3);
  const bgEndPos = new Float32Array(bgCount * 3);
  const bgRandoms = new Float32Array(bgCount);
  const bgAlphas = new Float32Array(bgCount);

  for (let i = 0, j = 0; i < bgCount; i++, j += 3) {
    const x = (Math.random() - 0.5) * spread;
    const y = (Math.random() - 0.5) * spread;
    const z = (Math.random() - 0.5) * spread;

    bgEndPos[j] = x;
    bgEndPos[j + 1] = y;
    bgEndPos[j + 2] = z;

    bgPosData[j] = x;
    bgPosData[j + 1] = y;
    bgPosData[j + 2] = z;

    bgRandoms[i] = Math.random();
    bgAlphas[i] = 1.0;
  }

  const bgGeom = new THREE.BufferGeometry();
  bgGeom.setAttribute("position", new THREE.BufferAttribute(bgPosData, 3));
  bgGeom.setAttribute("aRandom", new THREE.BufferAttribute(bgRandoms, 1));
  bgGeom.setAttribute("aAlpha", new THREE.BufferAttribute(bgAlphas, 1));

  const bgMat = new THREE.ShaderMaterial({
    uniforms,
    vertexShader: appBgVert,
    fragmentShader: appBgFrag,
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
  });

  const bgParticles = new THREE.Points(bgGeom, bgMat);
  bgParticles.frustumCulled = false;
  scene.add(bgParticles);

  let points = null;
  let originalPositions = null;
  let velocities = null;
  let masses = null;

  function sampleModelPositionsReservoir(model, maxSamples) {
    const reservoirPos = new Float32Array(maxSamples * 3);
    const reservoirRand = new Float32Array(maxSamples);
    const tmp = new THREE.Vector3();
    let seen = 0;
    let filled = 0;

    model.traverse((child) => {
      if (!child.isMesh) return;
      const posAttr = child.geometry.attributes.position;
      child.updateWorldMatrix(true, false);
      const matrix = child.matrixWorld;
      for (let i = 0; i < posAttr.count; i++) {
        tmp.fromBufferAttribute(posAttr, i).applyMatrix4(matrix);
        seen++;
        if (filled < maxSamples) {
          const base = filled * 3;
          reservoirPos[base] = tmp.x;
          reservoirPos[base + 1] = tmp.y;
          reservoirPos[base + 2] = tmp.z;
          reservoirRand[filled] = Math.random();
          filled++;
        } else {
          const r = Math.floor(Math.random() * seen);
          if (r < maxSamples) {
            const base = r * 3;
            reservoirPos[base] = tmp.x;
            reservoirPos[base + 1] = tmp.y;
            reservoirPos[base + 2] = tmp.z;
            reservoirRand[r] = Math.random();
          }
        }
      }
    });

    return {
      positions: reservoirPos.subarray(0, filled * 3),
      randoms: reservoirRand.subarray(0, filled),
    };
  }

  loader.load(
    code,
    (gltf) => {
      const model = gltf.scene || gltf.scenes?.[0] || null;
      if (!model) {
        resolveReady();
        return;
      }

      model.scale.set(MODEL_SCALE, MODEL_SCALE, MODEL_SCALE);
      model.rotation.x = Math.PI / 2;
      model.position.x = MODEL_POS_X;
      model.position.y = MODEL_POS_Y;
      modelRoot.add(model);
      model.updateWorldMatrix(true, true);

      const sampled = sampleModelPositionsReservoir(model, MAX_POINTS);
      originalPositions = new Float32Array(sampled.positions.length);
      originalPositions.set(sampled.positions);
      const count = originalPositions.length / 3;

      const aRandom = new Float32Array(count);
      const aAlpha = new Float32Array(count);
      for (let i = 0; i < count; i++) {
        aRandom[i] = sampled.randoms[i] ?? Math.random();
        aAlpha[i] = 1.0;
      }

      const particlesGeom = new THREE.BufferGeometry();
      particlesGeom.setAttribute(
        "position",
        new THREE.BufferAttribute(new Float32Array(originalPositions), 3),
      );
      particlesGeom.setAttribute(
        "aRandom",
        new THREE.BufferAttribute(aRandom, 1),
      );
      particlesGeom.setAttribute(
        "aAlpha",
        new THREE.BufferAttribute(aAlpha, 1),
      );

      const particleMat = new THREE.ShaderMaterial({
        uniforms,
        vertexShader: allGlbVert,
        fragmentShader: appGlbFrag,
        transparent: true,
        depthTest: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
      });

      points = new THREE.Points(particlesGeom, particleMat);
      points.frustumCulled = false;

      velocities = new Float32Array(count * 3);
      masses = new Float32Array(count);
      for (let i = 0; i < count; i++) masses[i] = 0.8 + Math.random() * 0.6;

      raycaster.setFromCamera(mouseNDC, camera);
      raycaster.ray.intersectPlane(interactionPlane, mouseWorldPos);

      smoothedMouse.copy(mouseWorldPos);
      prevSmoothedMouse.copy(mouseWorldPos);

      modelRoot.add(points);
      modelRoot.remove(model);

      resolveReady();
    },
    undefined,
    (err) => {
      console.warn("Model load error:", err);
      resolveReady();
    },
  );

  let elapsed = 0;
  const modelAnimDuration = 2.0;
  let modelElapsed = 0;

  const smoothedMouse = new THREE.Vector3();
  const prevSmoothedMouse = new THREE.Vector3();
  const mouseVelocity = new THREE.Vector3();

  const INTERACT = {
    radius: isMobile ? 0.1 : 0.3,
    strength: isMobile ? 0.15 : 0.45,
    returnForce: 4.5,
    damping: 0.93,
    swirl: 0.8,
    mouseInfluence: 0.04,
    maxVelocity: 3.0,
    alphaBoost: 7.0,
    alphaMaxDisp: 0.35,
    mouseLerp: 0.15,
  };

  function update(dt) {
    elapsed += dt;
    uniforms.time.value = elapsed;

    if (points) {
      modelElapsed += dt;
      const t = Math.min(1, modelElapsed / modelAnimDuration);
      points.material.uniforms.progress.value = t;
    }

    if (points) {
      points.rotation.y = Math.sin(elapsed * 0.1) * 0.05;
    }
    bgParticles.rotation.y = elapsed * 0.01;
    bgParticles.rotation.x = elapsed * 0.01;

    if (!points || !originalPositions || !velocities) return;

    raycaster.setFromCamera(mouseNDC, camera);
    raycaster.ray.intersectPlane(interactionPlane, mouseWorldPos);

    smoothedMouse.lerp(mouseWorldPos, INTERACT.mouseLerp);
    mouseVelocity
      .subVectors(smoothedMouse, prevSmoothedMouse)
      .divideScalar(Math.max(dt, 1e-6));
    prevSmoothedMouse.copy(smoothedMouse);

    const posArr = points.geometry.attributes.position.array;
    const alphaArr = points.geometry.attributes.aAlpha.array;
    const orig = originalPositions;
    const vel = velocities;
    const m = masses;
    const count = alphaArr.length;
    const r = INTERACT.radius;
    const elapsedLocal = elapsed;
    const dampFactor = Math.pow(INTERACT.damping, dt * 60);
    const maxV = INTERACT.maxVelocity;

    const currentSpeed = Math.sqrt(
      mouseVelocity.x * mouseVelocity.x + mouseVelocity.y * mouseVelocity.y,
    );
    const speedFactor = Math.min(currentSpeed * 0.04, 0.3);
    const maxDynamicR = r * (1.0 + 0.4 + speedFactor);
    const maxDynamicRSq = maxDynamicR * maxDynamicR;
    const alphaMaxDispSq = INTERACT.alphaMaxDisp * INTERACT.alphaMaxDisp;

    let particlesDirty = false;

    for (let i = 0, i3 = 0; i < count; i++, i3 += 3) {
      let px = posArr[i3];
      let py = posArr[i3 + 1];
      let pz = posArr[i3 + 2];
      let vx = vel[i3];
      let vy = vel[i3 + 1];
      let vz = vel[i3 + 2];

      const dx = px - smoothedMouse.x;
      const dy = py - smoothedMouse.y;
      const distSq = dx * dx + dy * dy;

      if (distSq < maxDynamicRSq) {
        const angle = Math.atan2(dy, dx);
        const organicDeformation =
          Math.sin(angle * 5.0 - elapsedLocal * 4.0) * 0.25 +
          Math.cos(angle * 3.0 + elapsedLocal * 2.5) * 0.15;
        const dynamicR = r * (1.0 + organicDeformation + speedFactor);

        if (distSq < dynamicR * dynamicR) {
          const dist = Math.sqrt(distSq) || 1e-4;
          let falloff = 1.0 - dist / dynamicR;
          falloff *= falloff;

          const dirX = dx / dist;
          const dirY = dy / dist;
          const perpX = -dirY;
          const perpY = dirX;
          const swirl =
            Math.sin(i * 0.37 + elapsedLocal * 4.0) * INTERACT.swirl * falloff;

          vx +=
            ((dirX * INTERACT.strength +
              perpX * swirl +
              mouseVelocity.x * INTERACT.mouseInfluence) *
              falloff) /
            m[i];
          vy +=
            ((dirY * INTERACT.strength +
              perpY * swirl +
              mouseVelocity.y * INTERACT.mouseInfluence) *
              falloff) /
            m[i];
        }
      }

      const ox = orig[i3];
      const oy = orig[i3 + 1];
      const oz = orig[i3 + 2];
      vx += (ox - px) * INTERACT.returnForce * dt;
      vy += (oy - py) * INTERACT.returnForce * dt;
      vz += (oz - pz) * INTERACT.returnForce * dt;
      vx *= dampFactor;
      vy *= dampFactor;
      vz *= dampFactor;
      const speedSq = vx * vx + vy * vy + vz * vz;
      if (speedSq > maxV * maxV) {
        const scale = maxV / Math.sqrt(speedSq);
        vx *= scale;
        vy *= scale;
        vz *= scale;
      }
      px += vx * dt;
      py += vy * dt;
      pz += vz * dt;
      posArr[i3] = px;
      posArr[i3 + 1] = py;
      posArr[i3 + 2] = pz;
      vel[i3] = vx;
      vel[i3 + 1] = vy;
      vel[i3 + 2] = vz;

      const offX = px - ox;
      const offY = py - oy;
      const dispSq = offX * offX + offY * offY;
      const tt = Math.min(dispSq / alphaMaxDispSq, 1.0);
      const smooth = tt * tt * (3 - 2 * tt);
      alphaArr[i] = 1.0 + smooth * INTERACT.alphaBoost;

      if (speedSq > 1e-6) particlesDirty = true;
    }

    if (particlesDirty) {
      points.geometry.attributes.position.needsUpdate = true;
      points.geometry.attributes.aAlpha.needsUpdate = true;
    }
  }

  const dummyRT = new THREE.WebGLRenderTarget(1, 1);

  function renderForSnapshot(rendererRef, targetRT, cam = camera) {
    if (points) points.material.uniforms.progress.value = 1.0;

    const prevBloomStrength = bloomPass.strength;
    bloomPass.strength = 1.0;
    modelElapsed = modelAnimDuration;

    update(0.01);

    const prevW = sizes.width;
    const prevH = sizes.height;
    const prevRT = rendererRef.getRenderTarget();
    const prevComposerRenderToScreen = composer.renderToScreen;

    composer.renderToScreen = false;
    const passStates = composer.passes.map((p) => p.renderToScreen);
    composer.passes.forEach((p) => {
      if ("renderToScreen" in p) p.renderToScreen = false;
    });

    composer.setSize(
      targetRT.width / rendererRef.getPixelRatio(),
      targetRT.height / rendererRef.getPixelRatio(),
    );

    rendererRef.setRenderTarget(composer.readBuffer);
    rendererRef.clear(true, true, true);
    rendererRef.setRenderTarget(composer.writeBuffer);
    rendererRef.clear(true, true, true);

    composer.render();

    const copyMatLocal = new THREE.MeshBasicMaterial({
      map: composer.writeBuffer.texture,
      depthTest: false,
      depthWrite: false,
    });
    const copyMeshLocal = new THREE.Mesh(
      new THREE.PlaneGeometry(2, 2),
      copyMatLocal,
    );
    const tmpScene = new THREE.Scene();
    const tmpCam = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
    tmpScene.add(copyMeshLocal);

    rendererRef.setRenderTarget(dummyRT);
    rendererRef.clear();

    rendererRef.setRenderTarget(targetRT);
    rendererRef.clear(true, true, true);
    rendererRef.render(tmpScene, tmpCam);

    composer.renderToScreen = prevComposerRenderToScreen;
    composer.passes.forEach((p, i) => {
      if ("renderToScreen" in p) p.renderToScreen = passStates[i];
    });
    composer.setSize(prevW, prevH);
    rendererRef.setRenderTarget(prevRT);

    bloomPass.strength = prevBloomStrength;

    copyMatLocal.dispose();
    copyMeshLocal.geometry.dispose();
  }

  function handlePointerMove(event) {
    mouseNDC.x = (event.clientX / window.innerWidth) * 2 - 1;
    mouseNDC.y = -(event.clientY / window.innerHeight) * 2 + 1;
  }

  let touchStartY = 0;
  function onTouchStart(e) {
    touchStartY = e.touches[0].clientY;
  }
  function onTouchMove(e) {
    const touchY = e.touches[0].clientY;
    const delta = touchStartY - touchY;
    if (Math.abs(delta) < 20) return;
    touchStartY = touchY;
  }
  function onWheel(e) {
    if (Math.abs(e.deltaY) < 10) return;
  }

  const eventOptions = { passive: true };

  function bindEvents() {
    window.addEventListener("pointermove", handlePointerMove, eventOptions);
    window.addEventListener("wheel", onWheel, eventOptions);
    window.addEventListener("touchstart", onTouchStart, eventOptions);
    window.addEventListener("touchmove", onTouchMove, eventOptions);
  }

  function unbindEvents() {
    window.removeEventListener("pointermove", handlePointerMove);
    window.removeEventListener("wheel", onWheel);
    window.removeEventListener("touchstart", onTouchStart);
    window.removeEventListener("touchmove", onTouchMove);
  }

  function activate() {
    mouseNDC.set(1, 2);
    raycaster.setFromCamera(mouseNDC, camera);
    raycaster.ray.intersectPlane(interactionPlane, mouseWorldPos);
    smoothedMouse.copy(mouseWorldPos);
    prevSmoothedMouse.copy(mouseWorldPos);
    bindEvents();
  }

  function deactivate() {
    unbindEvents();
  }

  function onWindowResize(w, h) {
    const width = Math.max(1, w);
    const height = Math.max(1, h);

    camera.aspect = width / height;
    camera.updateProjectionMatrix();

    composer.setSize(width, height);
    uniforms.pixelRatio.value = renderer.getPixelRatio();
  }

  const CAMERA_BASE_Z = 3;

  const CAMERA_POS = {
    step1: {
      x: isMobile ? 0.5 : -1.5,
      y: isMobile ? -1.0 : 0,
      z: isMobile ? 0 : CAMERA_BASE_Z - 2.5,
    },
    step2: {
      x: isMobile ? 0.5 : 1.5,
      y: isMobile ? 1.0 : 0,
      z: isMobile ? 0 : CAMERA_BASE_Z - 2.5,
    },
    base: {
      x: 0,
      y: 0,
      z: CAMERA_BASE_Z,
    },
  };

  const D = { dur: 1.5, ease: "power2.inOut" };

  const onUpdateLookAt = () => {
    camera.lookAt(modelRoot.position);
  };

  const zoomIn0 = () => {
    const tl = gsap.timeline({ onUpdate: onUpdateLookAt });
    tl.to(
      camera.position,
      {
        x: CAMERA_POS.step1.x,
        y: CAMERA_POS.step1.y,
        z: CAMERA_POS.step1.z,
        duration: D.dur,
        ease: D.ease,
      },
      0,
    );
    return tl;
  };

  const zoomIn1 = () => {
    const tl = gsap.timeline({ onUpdate: onUpdateLookAt });
    tl.to(
      camera.position,
      {
        x: CAMERA_POS.step2.x,
        y: CAMERA_POS.step2.y,
        z: CAMERA_POS.step2.z,
        duration: D.dur,
        ease: D.ease,
      },
      0,
    );
    return tl;
  };

  const zoomIn2 = () => {
    const tl = gsap.timeline({ onUpdate: onUpdateLookAt });
    tl.to(
      camera.position,
      {
        x: CAMERA_POS.base.x,
        y: CAMERA_POS.base.y,
        z: CAMERA_POS.base.z,
        duration: D.dur,
        ease: D.ease,
      },
      0,
    );
    return tl;
  };

  const zoomOut2 = () => zoomIn1();
  const zoomOut1 = () => zoomIn0();
  const zoomOut0 = () => zoomIn2();

  function setSubStep(step) {
    gsap.killTweensOf(camera.position);

    let target;
    switch (step) {
      case 1:
        target = CAMERA_POS.step1;
        break;
      case 2:
        target = CAMERA_POS.step2;
        break;
      case 0:
      case 3:
        target = CAMERA_POS.base;
        break;
      default:
        target = CAMERA_POS.base;
    }

    camera.position.set(target.x, target.y, target.z);
    camera.lookAt(modelRoot.position);
  }

  function hideLoader() {
    const el =
      typeof document !== "undefined" && document.getElementById("loader");
    if (el) el.style.display = "none";
  }

  function dispose() {
    if (points) {
      points.geometry.dispose();
      points.material.dispose();
    }
    if (bgGeom) bgGeom.dispose();
    if (bgMat) bgMat.dispose();
    if (composer && composer.dispose) composer.dispose();
    if (dummyRT) dummyRT.dispose();
  }
  onWindowResize(sizes.width, sizes.height);
  return {
    scene,
    update,
    activate,
    deactivate,
    zoomIn0,
    zoomIn1,
    zoomIn2,
    zoomOut0,
    zoomOut1,
    zoomOut2,
    setSubStep,
    ready,
    renderForSnapshot,
    onResize: onWindowResize,
    hideLoader,
    composer,
    dispose,
  };
}
